import React, { useState, useEffect, useRef } from 'react';
import ApiClient from '../../utils/ApiClient';
import { getUserName } from '../../auth';
import toast from 'react-hot-toast';

export default function CaseComments({ caseId, onCommentAdded }) {
    const [comments, setComments] = useState([]);
    const [users, setUsers] = useState([]);
    const [message, setMessage] = useState('');
    const [mentionedUsers, setMentionedUsers] = useState([]);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);
    const commentsEndRef = useRef(null);

    useEffect(() => {
        fetchComments();
        fetchUsers();
    }, [caseId]);

    useEffect(() => {
        scrollToBottom();
    }, [comments]);

    const scrollToBottom = () => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchComments = async () => {
        try {
            const response = await ApiClient.get(`/api/qrcases/${caseId}/comments`);
            setComments(response.data || []);
        } catch (error) {
            console.error('Error fetching comments:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await ApiClient.get('/api/users/mentionable');
            setUsers(response.data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setMessage(value);

        // Check for @ mentions
        const lastAtIndex = value.lastIndexOf('@');
        if (lastAtIndex !== -1) {
            const afterAt = value.substring(lastAtIndex + 1);
            const spaceAfterAt = afterAt.indexOf(' ');
            if (spaceAfterAt === -1) {
                setMentionFilter(afterAt.toLowerCase());
                setShowMentionDropdown(true);
                return;
            }
        }
        setShowMentionDropdown(false);
    };

    const handleMentionSelect = (user) => {
        const lastAtIndex = message.lastIndexOf('@');
        const beforeAt = message.substring(0, lastAtIndex);
        const newMessage = `${beforeAt}@${user.name} `;
        setMessage(newMessage);
        setShowMentionDropdown(false);

        if (!mentionedUsers.find(u => u.id === user.id)) {
            setMentionedUsers([...mentionedUsers, user]);
        }

        inputRef.current?.focus();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim() || submitting) return;

        setSubmitting(true);
        try {
            const response = await ApiClient.post(`/api/qrcases/${caseId}/comments`, {
                message: message.trim(),
                mentionedUserIds: mentionedUsers.map(u => u.id)
            });

            if (response.data.success) {
                const newComment = {
                    id: response.data.commentId,
                    user_name: response.data.userName || getUserName(),
                    message: message.trim(),
                    created_at: new Date().toISOString()
                };
                setComments([...comments, newComment]);
                setMessage('');
                setMentionedUsers([]);
                toast.success('Comment added');
                onCommentAdded?.();
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            toast.error('Failed to add comment');
        } finally {
            setSubmitting(false);
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const renderMessageWithMentions = (text) => {
        // Replace @Name with styled mentions
        const mentionRegex = /@(\w+(?:\s\w+)?)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }
            parts.push(
                <span key={match.index} className="bg-blue-100 text-blue-800 px-1 rounded">
                    @{match[1]}
                </span>
            );
            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(mentionFilter)
    );

    if (loading) {
        return (
            <div className="p-4 text-center text-gray-500">
                Loading comments...
            </div>
        );
    }

    return (
        <div className="border-t border-gray-200 mt-4">
            <div className="p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Comments ({comments.length})
                </h4>

                {/* Comments list */}
                <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                    {comments.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No comments yet. Be the first to comment!</p>
                    ) : (
                        comments.map((comment) => (
                            <div key={comment.id} className="flex gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-medium text-gray-600">
                                        {(comment.user_name || 'U').charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-sm font-medium text-gray-900">
                                            {comment.user_name || 'Unknown'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {formatTime(comment.created_at)}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-700 mt-0.5">
                                        {renderMessageWithMentions(comment.message)}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                    <div ref={commentsEndRef} />
                </div>

                {/* Comment input */}
                <form onSubmit={handleSubmit} className="relative">
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <input
                                ref={inputRef}
                                type="text"
                                value={message}
                                onChange={handleInputChange}
                                placeholder="Type a message... Use @ to mention"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={submitting}
                            />

                            {/* Mention dropdown */}
                            {showMentionDropdown && filteredUsers.length > 0 && (
                                <div className="absolute bottom-full left-0 mb-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                                    {filteredUsers.slice(0, 5).map((user) => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => handleMentionSelect(user)}
                                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                        >
                                            <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                                                <span className="text-xs font-medium text-gray-600">
                                                    {user.name?.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            {user.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={!message.trim() || submitting}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Sending...' : 'Send'}
                        </button>
                    </div>

                    {/* Quick mention chips */}
                    <div className="mt-2 flex flex-wrap gap-1">
                        <span className="text-xs text-gray-500">Quick mention:</span>
                        {['Steven', 'Shubham', 'Don', 'Bo'].map((name) => {
                            const user = users.find(u => u.name?.toLowerCase().includes(name.toLowerCase()));
                            return (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => {
                                        setMessage(message + `@${name} `);
                                        if (user && !mentionedUsers.find(u => u.id === user.id)) {
                                            setMentionedUsers([...mentionedUsers, user]);
                                        }
                                        inputRef.current?.focus();
                                    }}
                                    className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                >
                                    @{name}
                                </button>
                            );
                        })}
                    </div>
                </form>
            </div>
        </div>
    );
}
